import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import auditLogService from '../../../services/auditLogService';
import AppIcon from '../../../components/AppIcon';
import Select from '../../../components/ui/Select';

const RealtimeUpdateLogs = ({ user, refreshTrigger, onEvent }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all');
  const [subscription, setSubscription] = useState(null);

  useEffect(() => {
    if (user?.id) {
      loadAuditLogs();
      setupRealtimeSubscription();
    }

    return () => {
      if (subscription) {
        subscription?.unsubscribe();
      }
    };
  }, [user, filter, refreshTrigger]);

  const loadAuditLogs = async () => {
    setLoading(true);
    onEvent?.('Loading audit logs', 'info');

    try {
      const result = await auditLogService?.getUserAuditLogs(user?.id, {
        limit: 50,
        actionType: filter === 'all' ? null : filter,
        orderBy: 'created_at',
        orderDirection: 'desc'
      });

      if (result?.success) {
        setLogs(result?.data || []);
        onEvent?.(`Loaded ${result?.data?.length} audit logs`, 'success');
      } else {
        onEvent?.('Failed to load audit logs', 'error', result?.error);
      }
    } catch (error) {
      onEvent?.('Audit log load exception', 'error', error);
      console.error('Failed to load audit logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const setupRealtimeSubscription = () => {
    const channel = supabase?.channel(`audit-logs-${user?.id}`)?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'audit_logs',
          filter: `user_id=eq.${user?.id}`
        },
        (payload) => {
          onEvent?.('New audit log received', 'realtime', payload);
          setLogs(prev => [payload?.new, ...prev]);
        }
      )?.subscribe();

    setSubscription(channel);
    onEvent?.('Audit log real-time subscription established', 'info');
  };

  const getSeverityColor = (log) => {
    if (!log?.success) return 'border-red-500 bg-red-900/20';
    
    const severityMap = {
      profile_updated: 'border-blue-500 bg-blue-900/20',
      profile_created: 'border-green-500 bg-green-900/20',
      login: 'border-green-500 bg-green-900/20',
      logout: 'border-orange-500 bg-orange-900/20',
      profile_viewed: 'border-gray-500 bg-gray-900/20'
    };

    return severityMap?.[log?.action_type] || 'border-gray-500 bg-gray-900/20';
  };

  const getSeverityIcon = (log) => {
    if (!log?.success) return { name: 'XCircle', color: 'text-red-500' };
    
    const iconMap = {
      profile_updated: { name: 'Edit', color: 'text-blue-500' },
      profile_created: { name: 'Plus', color: 'text-green-500' },
      login: { name: 'LogIn', color: 'text-green-500' },
      logout: { name: 'LogOut', color: 'text-orange-500' },
      profile_viewed: { name: 'Eye', color: 'text-gray-500' }
    };

    return iconMap?.[log?.action_type] || { name: 'Activity', color: 'text-gray-500' };
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date?.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      fractionalSecondDigits: 3
    });
  };

  const filterOptions = [
    { value: 'all', label: 'All Operations' },
    { value: 'profile_updated', label: 'Updates' },
    { value: 'profile_created', label: 'Creates' },
    { value: 'login', label: 'Logins' },
    { value: 'logout', label: 'Logouts' },
    { value: 'profile_viewed', label: 'Views' }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white">Real-time Update Logs</h2>
          <p className="text-sm text-gray-400 mt-1">
            Streaming database operations with execution details
          </p>
        </div>
        <div className="w-64">
          <Select
            label="Filter Operations"
            value={filter}
            onChange={setFilter}
            options={filterOptions}
          />
        </div>
      </div>
      {/* Logs Stream */}
      {loading ? (
        <div className="flex items-center justify-center py-12 bg-gray-800 rounded-lg border border-gray-700">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500"></div>
        </div>
      ) : logs?.length === 0 ? (
        <div className="bg-gray-800 rounded-lg border border-gray-700 p-8 text-center">
          <AppIcon name="Inbox" className="w-12 h-12 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400">No logs found</p>
        </div>
      ) : (
        <div className="space-y-2">
          {logs?.map((log) => {
            const icon = getSeverityIcon(log);
            return (
              <div
                key={log?.id}
                className={`bg-gray-800 rounded-lg border-l-4 p-4 ${getSeverityColor(log)}`}
              >
                <div className="flex items-start gap-4">
                  <AppIcon name={icon?.name} className={`w-5 h-5 ${icon?.color} flex-shrink-0 mt-0.5`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-4">
                      <div className="font-medium text-white">{log?.action_description}</div>
                      <div className="text-xs text-gray-400 font-mono flex-shrink-0">
                        {formatTimestamp(log?.created_at)}
                      </div>
                    </div>

                    {/* Changed Fields */}
                    {log?.changed_fields && log?.changed_fields?.length > 0 && (
                      <div className="mt-2 flex items-center gap-2">
                        <span className="text-xs text-gray-400">Fields:</span>
                        <div className="flex flex-wrap gap-1">
                          {log?.changed_fields?.map((field) => (
                            <span
                              key={field}
                              className="text-xs bg-gray-700 text-gray-300 px-2 py-0.5 rounded"
                            >
                              {field}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Error Message */}
                    {log?.error_message && (
                      <div className="mt-2 text-sm text-red-400 bg-red-900/30 rounded px-3 py-2">
                        <div className="font-medium">Error:</div>
                        <div className="font-mono text-xs mt-1">{log?.error_message}</div>
                      </div>
                    )}

                    {/* Technical Details */}
                    <details className="mt-3">
                      <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-400">
                        Technical Details
                      </summary>
                      <div className="mt-2 bg-gray-900 rounded p-3 space-y-2 text-xs font-mono">
                        {log?.table_name && (
                          <div className="flex gap-2">
                            <span className="text-gray-500">Table:</span>
                            <span className="text-cyan-400">{log?.table_name}</span>
                          </div>
                        )}
                        {log?.record_id && (
                          <div className="flex gap-2">
                            <span className="text-gray-500">Record ID:</span>
                            <span className="text-gray-300">{log?.record_id}</span>
                          </div>
                        )}
                        {log?.ip_address && (
                          <div className="flex gap-2">
                            <span className="text-gray-500">IP:</span>
                            <span className="text-gray-300">{log?.ip_address}</span>
                          </div>
                        )}
                        {log?.session_id && (
                          <div className="flex gap-2">
                            <span className="text-gray-500">Session:</span>
                            <span className="text-gray-300">{log?.session_id}</span>
                          </div>
                        )}
                      </div>
                    </details>

                    {/* Value Changes */}
                    {log?.old_values && log?.new_values && (
                      <details className="mt-2">
                        <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-400">
                          View Value Changes
                        </summary>
                        <div className="mt-2 bg-gray-900 rounded p-3 space-y-2 text-xs font-mono">
                          {log?.changed_fields?.map((field) => (
                            <div key={field} className="border-l-2 border-blue-500 pl-3 py-1">
                              <div className="text-gray-400 mb-1">{field}</div>
                              <div className="text-red-400">- {JSON.stringify(log?.old_values?.[field])}</div>
                              <div className="text-green-400">+ {JSON.stringify(log?.new_values?.[field])}</div>
                            </div>
                          ))}
                        </div>
                      </details>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default RealtimeUpdateLogs;